import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Play, 
  Loader2, 
  ExternalLink, 
  CheckCircle2, 
  LogOut, 
  User,
  MessageSquare,
  Phone,
  Calendar,
  Clock,
  Zap,
  ChevronRight,
  Sparkles,
  BookOpen,
  Settings,
  BarChart3,
  PhoneIncoming,
  PhoneOutgoing,
  Bot,
  Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Sample demo conversations for different industries
const demoConversations = {
  hvac: {
    title: "HVAC Emergency",
    industry: "HVAC & Heating",
    messages: [
      { role: "customer", text: "Hi, my furnace just stopped working and it's -15 outside!", time: "2:34 PM" },
      { role: "ai", text: "I understand this is urgent - a furnace failure in this weather is an emergency. I'm here to help get you warm again quickly.\n\nCan you tell me: Is your thermostat showing any error codes or is the display blank?", time: "2:34 PM" },
      { role: "customer", text: "The display shows E3 error", time: "2:35 PM" },
      { role: "ai", text: "Thank you - an E3 code typically indicates an ignition failure, which our technicians can usually resolve same-day.\n\nI have emergency slots available today:\n• 3:30 PM - 5:00 PM\n• 5:30 PM - 7:00 PM\n\nWhich works better for you? I'll also need your address to dispatch our nearest technician.", time: "2:35 PM" },
      { role: "customer", text: "3:30 works. I'm at 1234 Frost Lane, Anchorage", time: "2:36 PM" },
      { role: "ai", text: "Perfect! I've booked you for 3:30 PM today at 1234 Frost Lane.\n\n✅ Technician: Mike R. (15 years experience)\n✅ ETA: 3:30 PM\n✅ Emergency service fee: $89 diagnostic\n\nMike will call you 15 minutes before arrival. Stay warm - help is on the way!\n\nIs there anything else I can help with?", time: "2:36 PM" }
    ]
  },
  plumbing: {
    title: "Burst Pipe",
    industry: "Plumbing",
    messages: [
      { role: "customer", text: "HELP! I have water spraying everywhere from a pipe under my sink!", time: "8:15 AM" },
      { role: "ai", text: "I'm on it! First, let's stop the water:\n\n🔴 URGENT: Turn off the water valve under the sink (rotate clockwise) or your main water shutoff.\n\nHave you been able to stop the water flow?", time: "8:15 AM" },
      { role: "customer", text: "Yes just turned it off. There's water everywhere", time: "8:16 AM" },
      { role: "ai", text: "Great job stopping the water! That prevents further damage.\n\nI'm dispatching an emergency plumber to you now. We have someone available within 45 minutes.\n\nWhile you wait:\n• Place towels around the area\n• If safe, take photos for insurance\n\nWhat's your address?", time: "8:16 AM" },
      { role: "customer", text: "789 Glacier Way, Apt 4B", time: "8:17 AM" },
      { role: "ai", text: "Confirmed! Emergency plumber en route:\n\n✅ Technician: Sarah K.\n✅ ETA: 9:00 AM (43 minutes)\n✅ Direct line: She'll call when 10 min away\n\nEmergency burst pipe repairs typically run $150-300 depending on the fix needed.\n\nSarah is one of our best - she'll have you back to normal quickly. Hang in there!", time: "8:17 AM" }
    ]
  },
  dental: {
    title: "Appointment Booking",
    industry: "Dental Office",
    messages: [
      { role: "customer", text: "Hi, I need to schedule a cleaning. Do you have anything next week?", time: "10:22 AM" },
      { role: "ai", text: "Hi! I'd be happy to help you schedule a cleaning.\n\nI have these openings next week:\n\n📅 Monday 1/15: 9:00 AM, 2:30 PM\n📅 Tuesday 1/16: 11:00 AM, 4:00 PM\n📅 Thursday 1/18: 10:00 AM, 3:00 PM\n\nDo any of these work for you? Cleanings typically take about 45 minutes.", time: "10:22 AM" },
      { role: "customer", text: "Tuesday at 11 works", time: "10:23 AM" },
      { role: "ai", text: "Perfect! I've reserved Tuesday, January 16th at 11:00 AM for your cleaning.\n\nA few quick questions:\n1. Are you a new patient or returning?\n2. Do you have dental insurance you'd like us to bill?", time: "10:23 AM" },
      { role: "customer", text: "Returning patient - John Smith. Yes I have Delta Dental", time: "10:24 AM" },
      { role: "ai", text: "Welcome back, John! I found your file.\n\n✅ Appointment Confirmed:\n• Tuesday, Jan 16 at 11:00 AM\n• Teeth Cleaning (45 min)\n• Dr. Williams\n• Delta Dental on file\n\nYou'll receive a reminder text 24 hours before. See you Tuesday!\n\nIs there anything else I can help with?", time: "10:24 AM" }
    ]
  }
};

// Quick start guide steps
const quickStartSteps = [
  {
    step: 1,
    title: "Show the Problem",
    description: "Start by asking: 'How many calls did you miss last week?' Let them feel the pain.",
    icon: PhoneIncoming,
    talking_points: [
      "Most contractors miss 40-60% of calls during busy hours",
      "Each missed call = $500-2000 in lost revenue",
      "Your competitors answer when you can't"
    ]
  },
  {
    step: 2,
    title: "Live Demo",
    description: "Show a real conversation. Pick their industry from the demo scenarios.",
    icon: MessageSquare,
    talking_points: [
      "Watch how fast the AI responds (under 60 seconds)",
      "Notice how it qualifies urgency automatically",
      "See the appointment get booked in real-time"
    ]
  },
  {
    step: 3,
    title: "Explain the Tech",
    description: "Show the Control Panel. Explain how easy setup is.",
    icon: Settings,
    talking_points: [
      "Works with their existing phone number",
      "Syncs with Google Calendar / their scheduler",
      "No app to download, no training needed"
    ]
  },
  {
    step: 4,
    title: "Handle Objections",
    description: "Address common concerns confidently.",
    icon: CheckCircle2,
    talking_points: [
      "'It sounds robotic' → Play the demo, it's conversational",
      "'What if it messes up?' → 100% money-back guarantee",
      "'I need to think about it' → Calculate their weekly losses"
    ]
  },
  {
    step: 5,
    title: "Close the Deal",
    description: "Create urgency and make it easy to say yes.",
    icon: Sparkles,
    talking_points: [
      "Setup takes 48 hours - you could be capturing leads by Friday",
      "One-time $1,997 setup, then $397/month optional",
      "ROI: Just 2 extra jobs pays for a full year"
    ]
  }
];

export default function DemoDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [status, setStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [activeDemo, setActiveDemo] = useState('hvac');
  const [demoPlaying, setDemoPlaying] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [activeTab, setActiveTab] = useState('demo');

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        checkOpenClawStatus();
      } catch (e) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]); // checkOpenClawStatus defined below, intentional

  const checkOpenClawStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await fetch(`${API}/openclaw/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      console.error('Status check failed:', e);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with navigation even if logout request fails
    }
    navigate('/login', { replace: true });
  };

  const goToControlUI = async () => {
    try {
      const res = await fetch(`${API}/openclaw/token`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const gatewayWsUrl = `${wsProtocol}//${window.location.host}/api/openclaw/ws`;
        window.location.href = `${API}/openclaw/ui/?gatewayUrl=${encodeURIComponent(gatewayWsUrl)}&token=${encodeURIComponent(data.token)}`;
      } else {
        toast.error('Unable to get access token');
      }
    } catch (e) {
      toast.error('Failed to access Control UI');
    }
  };

  const goToOldSetup = () => {
    navigate('/setup');
  };

  // Play demo conversation animation
  const playDemo = () => {
    setDemoPlaying(true);
    setVisibleMessages(0);
    
    const messages = demoConversations[activeDemo].messages;
    let index = 0;
    
    const showNext = () => {
      if (index < messages.length) {
        setVisibleMessages(index + 1);
        index++;
        setTimeout(showNext, 1500);
      } else {
        setDemoPlaying(false);
      }
    };
    
    showNext();
  };

  const resetDemo = () => {
    setVisibleMessages(0);
    setDemoPlaying(false);
  };

  if (isAuthenticated === null || checkingStatus) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400 flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Alaska AI Services</h1>
                <p className="text-xs text-slate-500">Demo Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {status?.running && (
                <Button
                  onClick={goToControlUI}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                  data-testid="open-control-ui"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Control Panel
                </Button>
              )}
              
              <div className="flex items-center gap-2 text-sm text-slate-400">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">{user?.name}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Status Banner */}
        {status?.running ? (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">AI System Active</span>
              <span className="text-slate-500">•</span>
              <span className="text-slate-400 text-sm">Ready for demos</span>
            </div>
            <Button onClick={goToControlUI} variant="outline" size="sm" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
              Open Control Panel <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">AI System Offline</span>
              <span className="text-slate-400 text-sm">Start it to run live demos</span>
            </div>
            <Button onClick={goToOldSetup} className="bg-amber-500 hover:bg-amber-600 text-black">
              Start AI System
            </Button>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="demo" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Demo Mode
            </TabsTrigger>
            <TabsTrigger value="guide" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Quick Start Guide
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Demo Stats
            </TabsTrigger>
          </TabsList>

          {/* Demo Mode Tab */}
          <TabsContent value="demo" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Industry Selector */}
              <div className="lg:col-span-1 space-y-4">
                <h3 className="font-semibold text-lg">Select Industry</h3>
                <div className="space-y-2">
                  {Object.entries(demoConversations).map(([key, conv]) => (
                    <button
                      key={key}
                      onClick={() => { setActiveDemo(key); resetDemo(); }}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        activeDemo === key 
                          ? 'bg-sky-500/10 border-sky-500/50 text-sky-400' 
                          : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                      }`}
                      data-testid={`demo-select-${key}`}
                    >
                      <div className="font-medium">{conv.industry}</div>
                      <div className="text-sm text-slate-500">{conv.title}</div>
                    </button>
                  ))}
                </div>
                
                <div className="pt-4 space-y-3">
                  <Button 
                    onClick={playDemo} 
                    disabled={demoPlaying}
                    className="w-full bg-sky-500 hover:bg-sky-600"
                    data-testid="play-demo-btn"
                  >
                    {demoPlaying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Playing...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Play Demo Conversation
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    onClick={resetDemo} 
                    variant="outline" 
                    className="w-full border-slate-700"
                    data-testid="reset-demo-btn"
                  >
                    Reset Demo
                  </Button>
                </div>
              </div>

              {/* Conversation Display */}
              <div className="lg:col-span-2">
                <Card className="bg-slate-900/50 border-slate-800 h-[600px] flex flex-col">
                  <CardHeader className="border-b border-slate-800 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-sky-400" />
                        </div>
                        <div>
                          <CardTitle className="text-base">Alaska AI Assistant</CardTitle>
                          <CardDescription className="text-xs">
                            {demoConversations[activeDemo].industry} Demo
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>Response: &lt;60 sec</span>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                    <AnimatePresence>
                      {demoConversations[activeDemo].messages.slice(0, visibleMessages).map((msg, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex gap-3 ${msg.role === 'customer' ? 'justify-end' : ''}`}
                        >
                          {msg.role === 'ai' && (
                            <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                              <Bot className="w-4 h-4 text-sky-400" />
                            </div>
                          )}
                          <div className={`max-w-[80%] ${
                            msg.role === 'customer' 
                              ? 'bg-sky-500 text-white rounded-2xl rounded-tr-sm' 
                              : 'bg-slate-800 rounded-2xl rounded-tl-sm'
                          } px-4 py-3`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                            <p className={`text-xs mt-2 ${msg.role === 'customer' ? 'text-sky-200' : 'text-slate-500'}`}>
                              {msg.time}
                            </p>
                          </div>
                          {msg.role === 'customer' && (
                            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    
                    {visibleMessages === 0 && (
                      <div className="h-full flex items-center justify-center text-slate-500">
                        <div className="text-center">
                          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Click "Play Demo" to start the conversation</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Quick Start Guide Tab */}
          <TabsContent value="guide" className="space-y-6">
            <div className="grid gap-6">
              <div className="text-center max-w-2xl mx-auto mb-4">
                <h2 className="text-2xl font-bold mb-2">Demo Quick Start Guide</h2>
                <p className="text-slate-400">Follow these 5 steps to close more deals</p>
              </div>
              
              {quickStartSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
                            <step.icon className="w-6 h-6 text-sky-400" />
                          </div>
                          <div className="mt-2 text-center">
                            <span className="text-xs text-slate-500">Step {step.step}</span>
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                          <p className="text-slate-400 mb-4">{step.description}</p>
                          
                          <div className="space-y-2">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Talking Points:</p>
                            {step.talking_points.map((point, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <ChevronRight className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-slate-300">{point}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <PhoneIncoming className="w-5 h-5 text-sky-400" />
                    <span className="text-slate-400 text-sm">Leads Captured</span>
                  </div>
                  <p className="text-3xl font-bold">--</p>
                  <p className="text-xs text-slate-500 mt-1">Start AI system to track</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-green-400" />
                    <span className="text-slate-400 text-sm">Appointments Booked</span>
                  </div>
                  <p className="text-3xl font-bold">--</p>
                  <p className="text-xs text-slate-500 mt-1">Start AI system to track</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    <span className="text-slate-400 text-sm">Avg Response Time</span>
                  </div>
                  <p className="text-3xl font-bold">&lt;60s</p>
                  <p className="text-xs text-slate-500 mt-1">Guaranteed</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <span className="text-slate-400 text-sm">Conversion Rate</span>
                  </div>
                  <p className="text-3xl font-bold">--</p>
                  <p className="text-xs text-slate-500 mt-1">Start AI system to track</p>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8 text-center">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <h3 className="font-semibold mb-2">Analytics Coming Soon</h3>
                <p className="text-slate-400 text-sm max-w-md mx-auto">
                  Once the AI system is running and handling real conversations, you'll see detailed analytics here.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
